import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthFromToken } = useAuth();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pw !== confirm) return toast.error("Passwords don't match.");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      toast.success("Password updated. You're signed in.");
      if (setAuthFromToken) {
        setAuthFromToken(data.token, data.user);
      } else {
        // fallback: store token and reload
        localStorage.setItem("ordeneli_token", data.token);
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center py-16 px-4">
        <div className="w-full max-w-md border border-[#FECACA] bg-[#FEF2F2] p-6" data-testid="reset-invalid">
          <p className="font-display text-xl font-black">Invalid reset link.</p>
          <p className="mt-2 text-sm text-[#525252]">This link is missing a token. Please request a new one.</p>
          <Link to="/auth/forgot" className="btn-primary mt-5 inline-flex">Request new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-10 md:py-16 px-4">
      <div className="w-full max-w-md border border-[#E5E7EB] bg-white p-6 sm:p-8" data-testid="reset-password-page">
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">Reset password</p>
        <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Choose a new password</h1>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-4" data-testid="reset-password-form">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold">New password</label>
            <input
              type="password"
              required
              autoFocus
              minLength={8}
              className="input-line"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="At least 8 characters"
              data-testid="reset-new-password"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold">Confirm new password</label>
            <input
              type="password"
              required
              minLength={8}
              className="input-line"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm-password"
            />
          </div>
          <button type="submit" disabled={submitting} className="btn-primary justify-center" data-testid="reset-submit">
            {submitting ? "Updating…" : "Set new password"}
          </button>
        </form>
      </div>
    </div>
  );
}
